import { useState, useEffect, useCallback, useRef } from 'react'
import {
  readPayloadCache as readCache,
  writePayloadCache as writeCache,
} from '../utils/payloadCache'

interface UseCachedDataOptions<T> {
  cacheKey: string
  fetcher: () => Promise<{ success: boolean; data?: T; error?: { code: string; message: string } }>
  enabled?: boolean
}

interface UseCachedDataResult<T> {
  data: T | null
  isLoading: boolean
  error: string | null
  didRefresh: boolean
  refetch: () => Promise<void>
}

/**
 * Hook for React-layer localStorage caching.
 * Shows cached data immediately on mount, then fetches fresh data.
 * Sets `didRefresh` to true briefly when fresh data replaces cached data,
 * enabling a "blink" animation.
 */
export function useCachedData<T>({
  cacheKey,
  fetcher,
  enabled = true,
}: UseCachedDataOptions<T>): UseCachedDataResult<T> {
  const cached = readCache<T>(cacheKey)
  const [data, setData] = useState<T | null>(cached)
  const [isLoading, setIsLoading] = useState(!cached)
  const [error, setError] = useState<string | null>(null)
  const [didRefresh, setDidRefresh] = useState(false)
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>()

  const fetchData = useCallback(async () => {
    if (!enabled) return
    const hadCachedData = !!readCache<T>(cacheKey)

    if (!hadCachedData) {
      setIsLoading(true)
    }
    setError(null)

    try {
      const response = await fetcher()
      if (response.success && response.data) {
        writeCache(cacheKey, response.data)
        setData(response.data)

        // Trigger blink animation if we had cached data and got fresh data
        if (hadCachedData) {
          setDidRefresh(true)
          clearTimeout(refreshTimer.current)
          refreshTimer.current = setTimeout(() => setDidRefresh(false), 600)
        }
      } else {
        const msg = response.error?.message || 'Erro ao carregar dados'
        // Only show error if we have no cached data to display
        if (!hadCachedData) {
          setError(msg)
        }
      }
    } catch {
      if (!hadCachedData) {
        setError('Erro de conexão com o servidor')
      }
    } finally {
      setIsLoading(false)
    }
  }, [cacheKey, fetcher, enabled])

  useEffect(() => {
    fetchData()
    return () => clearTimeout(refreshTimer.current)
  }, [fetchData])

  return { data, isLoading, error, didRefresh, refetch: fetchData }
}
